// Phase 6a — conservative, client-side spam auto-hide.
//
// Scope: only hides Needs-You conversations whose latest inbound message
// matches the bulk-outreach pattern. Nothing touches the database; the row
// is still visible if Kimi clicks "Show N hidden". Because this runs purely
// on what's already in the Standup feed, a false positive has zero lasting
// impact — toggle show/hide and the thread is right there.
//
// Design principles:
//   1. Conservative by default. We'd rather leak 5 obvious spams into the
//      pile than hide 1 legitimate customer question.
//   2. Require TWO independent signals (phrase + structural) OR one very
//      high-confidence phrase that never shows up in real customer email.
//      Two categories:
//        • Strong phrases: near-zero false positive rate. One is enough.
//        • Weak phrases: fuzzy. Need a second signal to flag.
//   3. Match on body excerpt + subject. Both are already in the
//      Standup payload, so no extra fetch.

export interface SpamInput {
  subject: string | null;
  body: string;
}

/**
 * Phrases that essentially never appear in a real customer support email.
 * Match ANY one and we flag as spam. Keep this list tight and specific.
 * Add ONLY phrases that would be jarring from an actual MD customer.
 */
const STRONG_PHRASES: RegExp[] = [
  /\breach(?:ing|ed) out on behalf of\b/i,
  /\bwould love to (?:hop|jump) on a (?:quick )?call\b/i,
  /\bhop on a (?:quick )?(?:call|chat)\b/i,
  /\bi came across your (?:website|company|brand)\b/i,
  /\bpartnership (?:proposal|opportunity)\b/i,
  /\bunique (?:investment|business) opportunity\b/i,
  /\b(?:link[- ]building|guest post(?:ing)?) (?:opportunit(?:y|ies)|service)/i,
  /\bwe help businesses (?:like yours|similar to yours)\b/i,
  /\bi noticed your website (?:could|has|is missing)\b/i,
  /\bwould you be interested in speaking at\b/i,
];

/**
 * Phrases that sometimes appear in spam but occasionally in legit mail.
 * Need a SECOND weak signal to flag. Two weak hits = spam; one isn't enough.
 */
const WEAK_PHRASES: RegExp[] = [
  /\bSEO (?:services?|audit|agency)\b/i,
  /\bdigital marketing (?:services?|agency)\b/i,
  /\bgrow your (?:business|revenue|sales)\b/i,
  /\b(?:proprietary|revolutionary) (?:platform|solution|technology)\b/i,
  /\bbook a (?:free )?(?:demo|consultation|strategy call)\b/i,
  /\b(?:whitelisted?|white[- ]listed?) your domain\b/i,
  /\bcase stud(?:y|ies) (?:with|from) (?:brands|companies) like yours\b/i,
];

/**
 * Subject-line markers. Many true-positive spams have a very generic
 * "Quick question" / "Partnership with {Brand}" subject. Treated as
 * weak signals — don't flag alone.
 */
const WEAK_SUBJECT: RegExp[] = [
  /^(?:re: )?(?:quick question|partnership|collaboration|opportunity)\b/i,
  /^fwd?:.*(?:opportunity|partnership)\b/i,
];

export function isLikelySpam(input: SpamInput): boolean {
  const body = input.body ?? '';
  const subject = input.subject ?? '';

  // Strong hit — flag immediately.
  for (const rx of STRONG_PHRASES) {
    if (rx.test(body) || rx.test(subject)) return true;
  }

  // Two-weak-signal rule.
  let weakHits = 0;
  for (const rx of WEAK_PHRASES) {
    if (rx.test(body) || rx.test(subject)) {
      weakHits += 1;
      if (weakHits >= 2) return true;
    }
  }
  for (const rx of WEAK_SUBJECT) {
    if (rx.test(subject)) {
      weakHits += 1;
      if (weakHits >= 2) return true;
    }
  }

  return false;
}
